/**
 * EnrollmentWizard Component
 *
 * 4-step wizard for WebAuthn PRF credential enrollment.
 * Enables user-controlled encryption for L1/L1G PII fields.
 *
 * FERP-PII-052: User Enrollment Flow
 *
 * Steps:
 * 1. PRIMARY CREDENTIAL - Register biometric with PRF extension
 * 2. BACKUP DEVICE - Optional second credential
 * 3. RECOVERY PHRASE - Optional BIP39 recovery phrase
 * 4. COMPLETION - Summary and next steps
 *
 * @example
 * ```tsx
 * <EnrollmentWizard
 *   onComplete={() => navigate('/dashboard')}
 *   onSkip={() => navigate('/dashboard')}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import {
  Stepper,
  Paper,
  Stack,
  Text,
  Button,
  Group,
  Box,
  Alert,
  Center,
  Loader,
} from '@mantine/core';
import {
  IconFingerprint,
  IconDeviceMobile,
  IconKey,
  IconCheck,
  IconAlertCircle,
} from '@tabler/icons-react';

import {
  useEnrollment,
  EnrollmentPromptType,
  EnrollmentStatus,
  SkipReason,
  ENROLLMENT_STEP_IDS,
} from './hooks/useEnrollment';
import { PrimaryCredentialStep } from './steps/PrimaryCredentialStep';
import { BackupDeviceStep } from './steps/BackupDeviceStep';
import { RecoveryPhraseStep } from './steps/RecoveryPhraseStep';
import { CompletionStep } from './steps/CompletionStep';

export interface EnrollmentWizardProps {
  /** Callback when enrollment is complete */
  onComplete?: () => void;

  /** Callback when user skips enrollment */
  onSkip?: () => void;

  /** Allow skipping enrollment (default: true) */
  allowSkip?: boolean;

  /** Trigger type for enrollment */
  trigger?: EnrollmentPromptType;
}

/**
 * EnrollmentWizard Component
 *
 * Guides users through biometric enrollment with PRF support
 * for user-controlled PII encryption.
 */
export const EnrollmentWizard: React.FC<EnrollmentWizardProps> = ({
  onComplete,
  onSkip,
  allowSkip = true,
  trigger = EnrollmentPromptType.FIRST_LOGIN,
}) => {
  const {
    currentFlow,
    loading,
    error,
    startEnrollment,
    completeStep,
    skipCurrentStep,
    skipEnrollment,
    clearError,
  } = useEnrollment();

  const [active, setActive] = useState(0);
  const [initializing, setInitializing] = useState(true);

  // Initialize enrollment flow on mount
  useEffect(() => {
    const init = async () => {
      if (!currentFlow) {
        await startEnrollment(trigger);
      }
      setInitializing(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync active step with flow state
  useEffect(() => {
    if (currentFlow?.currentStep !== undefined) {
      setActive(currentFlow.currentStep);
    }
  }, [currentFlow?.currentStep]);

  // Handle enrollment completion
  useEffect(() => {
    if (currentFlow?.status === EnrollmentStatus.COMPLETED) {
      setActive(3); // Move to completion step
    }
  }, [currentFlow?.status]);

  /**
   * Map step index (0-3) to backend step ID
   */
  const getStepId = (index: number): string => {
    const stepIds = [
      ENROLLMENT_STEP_IDS.PRIMARY_CREDENTIAL,
      ENROLLMENT_STEP_IDS.BACKUP_DEVICE,
      ENROLLMENT_STEP_IDS.RECOVERY_PHRASE,
      ENROLLMENT_STEP_IDS.COMPLETION,
    ];
    return stepIds[index] || ENROLLMENT_STEP_IDS.PRIMARY_CREDENTIAL;
  };

  /**
   * Handle step completion
   *
   * @param credentialId - WebAuthn credential ID (for primary/backup steps)
   * @param prfOutput - PRF output base64 (required for primary_credential step)
   * @param _deviceName - Device name (not sent to backend, stored locally if needed)
   */
  const handleStepComplete = async (credentialId?: string, prfOutput?: string, _deviceName?: string) => {
    const stepId = getStepId(active);

    const result = await completeStep({
      stepId,
      resultData: credentialId, // Backend expects result_data for credential ID
      prfOutput,
    });

    if (result) {
      if (result.status === EnrollmentStatus.COMPLETED) {
        setActive(3); // Completion step
      } else {
        setActive((prev) => prev + 1);
      }
    }
  };

  /**
   * Handle step skip
   */
  const handleSkipStep = async () => {
    const result = await skipCurrentStep();
    if (result) {
      setActive((prev) => prev + 1);
    }
  };

  /**
   * Handle enrollment skip (Remind me later)
   */
  const handleSkipEnrollment = async () => {
    await skipEnrollment(SkipReason.REMIND_LATER);
    onSkip?.();
  };

  /**
   * Handle wizard completion
   */
  const handleComplete = () => {
    onComplete?.();
  };

  // Loading state
  if (initializing) {
    return (
      <Center h={400}>
        <Stack ta="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text size="sm" c="dimmed">Setting up enrollment...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Box maw={700} mx="auto" p="md">
      <Paper shadow="sm" p="xl" withBorder>
        <Stack gap="xl">
          {/* Header */}
          <Stack gap={4}>
            <Text size="xl" fw={600}>
              Secure Your Account
            </Text>
            <Text size="sm" c="dimmed">
              Set up biometric protection for your sensitive information (SSN, bank accounts).
            </Text>
          </Stack>

          {/* Error Alert */}
          {error && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Error"
              withCloseButton
              onClose={clearError}
            >
              {error}
            </Alert>
          )}

          {/* Stepper */}
          <Stepper
            active={active}
            onStepClick={setActive}
            allowNextStepsSelect={false}
          >
            {/* Step 1: Primary Credential */}
            <Stepper.Step
              label="Primary Biometric"
              description="Touch ID or Face ID"
              icon={<IconFingerprint size={18} />}
              loading={loading && active === 0}
            >
              <PrimaryCredentialStep
                onComplete={handleStepComplete}
                onSkip={handleSkipEnrollment}
                allowSkip={allowSkip}
                loading={loading}
              />
            </Stepper.Step>

            {/* Step 2: Backup Device */}
            <Stepper.Step
              label="Backup Device"
              description="Optional"
              icon={<IconDeviceMobile size={18} />}
              loading={loading && active === 1}
            >
              <BackupDeviceStep
                onComplete={handleStepComplete}
                onSkip={handleSkipStep}
                loading={loading}
              />
            </Stepper.Step>

            {/* Step 3: Recovery Phrase */}
            <Stepper.Step
              label="Recovery"
              description="Optional"
              icon={<IconKey size={18} />}
              loading={loading && active === 2}
            >
              <RecoveryPhraseStep
                onComplete={handleStepComplete}
                onSkip={handleSkipStep}
                loading={loading}
              />
            </Stepper.Step>

            {/* Completion */}
            <Stepper.Completed>
              <CompletionStep onComplete={handleComplete} />
            </Stepper.Completed>
          </Stepper>
        </Stack>
      </Paper>
    </Box>
  );
};

export default EnrollmentWizard;
